import React, { Fragment, useState } from 'react'
import styled from '@emotion/styled'
import { Button, Modal, Icon, Form } from 'antd'
import { ContentContainer } from 'components'
import { apiClient } from 'utils/api-client'
import { getAuthHeader } from 'utils/auth'
import ScanFilterTag from 'views/ScanSettings/components/ScanFilterTag'
import NewFilter from 'views/ScanSettings/Scan/components/NewFilter'
import SaveFilter from 'views/ScanSettings/Scan/components/SaveFilter'
import LoadFilter from 'views/ScanSettings/Scan/components/LoadFilter'
import { usePayment } from 'context/payment-context';


import { elements } from 'style/theme'
import { FILTER_NAME, FILTER_TYPE, FILTER_CONDITION, FILTER_VALUE } from 'views/ScanSettings/NewScan/constants'
import FormFooter from 'views/ScanSettings/components/ScanFilterBar/FormFooter'

const NEW_FILTER = 'new'
const SAVE_FILTER = 'save'
const LOAD_FILTER = 'load'

const okTexts = {
	[NEW_FILTER]: 'SAVE',
	[SAVE_FILTER]: 'SAVE',
	[LOAD_FILTER]: 'LOAD'
}

const formTitles = {
	[NEW_FILTER]: 'NEW FILTER ATTRIBUTE',
	[SAVE_FILTER]: null,
	[LOAD_FILTER]: 'LOAD FILTER'
}

const ScanSettingsFilterBar = styled.div({
	display: 'grid',
	gridTemplateColumns: '5fr 0.5fr 1fr',
	alignItems: 'center',
	gridColumnGap: 10
})

const ScanSettingsFilters = styled.div({
	display: 'flex',
	flexWrap: 'wrap',
	padding: '5px 1rem',
	overflowX: 'hidden',
	height: 58
})

const AddFilter = styled.div({
	borderRight: elements.brandBorder,
	padding: 15
})

const ScanSettingsFilterActions = styled.div({
	display: 'grid',
	gridTemplateColumns: '100px 1px 100px',
	gridColumnGap: 15,
	justifyItems: 'center',
	alignItems: 'center',
	padding: '0 1rem'
})

const StyledDivider = styled.div({
	height: 30,
	width: 1,
	border: '1px solid #ededed'
})

const FilterAction = styled(Button)({
	border: 'none',
	boxShadow: 'none'
})

const FiltersLoading = styled(Icon)({
	fontSize: 22,
	color: '#98d0ff'
})

const ScanFilterBar = ({ onFilterAttributesChange, filters, loading, onNewFilterSave, form, isScanCompare, isDetailsView }) => {
	const [filterModalVisible, setFilterModalVisible] = useState(false)
	const [currentFilterForm, setCurrentFilterForm] = useState(NEW_FILTER)
	const [filterAttributes, setFilterAttributes] = useState([])
	const {setPaymentModalOpen, isTrialAccount} = usePayment();

	const removeAttribute = attr => {
		let updated = filterAttributes.filter(a => !(a.type === attr.type && a.condition === attr.condition && a.value === attr.value))
		onFilterAttributesChange(updated)
		setFilterAttributes(updated)
	}
	const addFilterAttribute = attr => {
		let updated = [...filterAttributes, attr]
		onFilterAttributesChange(updated)
		setFilterAttributes(updated)
	}

	const saveFilterAttribute = async () => {
		try {
			const attribs = await form.validateFields()
			const newAttr = {
				type: attribs[FILTER_TYPE],
				condition: attribs[FILTER_CONDITION],
				value: attribs[FILTER_VALUE]
			}
			addFilterAttribute(newAttr)
			form.resetFields()
			setFilterModalVisible(false)
		} catch (err) {
			console.log(err)
		}
	}

	const saveFilter = async () => {
		try {
			const filterName = await form.validateFields([SAVE_FILTER])
			if(!!filterAttributes.length){
				onNewFilterSave({ name: filterName[SAVE_FILTER], attributes: filterAttributes })
				form.resetFields([SAVE_FILTER])
				setFilterModalVisible(false)
			}
			else {
				let value = form.getFieldValue(SAVE_FILTER);
				form.setFields({
					[SAVE_FILTER]: {
						value,
						errors: [new Error('Filter must be created first')]
					}
				})
			}
		} catch (err) {
			console.log(err)
		}
	}

	const loadFilter = async () => {
		try {
			const headers = getAuthHeader()
			const selectedFilter = await form.validateFields([LOAD_FILTER])
			const selected = selectedFilter[LOAD_FILTER]
			if (selected) {
				let filterSearch = filters.find(filter => filter.id === selected)
				filterSearch = filterSearch && await apiClient(`/global/filters/${filterSearch.id}`, { headers: headers } )
				let isValid = filterSearch.status === 200;
				let filterResult = isValid ? filterSearch.data : filterSearch;
				if (isValid && filterResult.properties) {
					setFilterAttributes(filterResult.properties)
					onFilterAttributesChange(filterResult.properties)
				}
			}
			form.resetFields([LOAD_FILTER])
			setFilterModalVisible(false)
		} catch (err) {
			console.log(err)
		}
	}

	const formCallbacks = {
		[NEW_FILTER]: saveFilterAttribute,
		[SAVE_FILTER]: saveFilter,
		[LOAD_FILTER]: loadFilter
	}

	return (
		<Fragment>
			<ContentContainer pad={false}>
				<ScanSettingsFilterBar>
					{loading ? (
						<FiltersLoading type="loading" />
					) : (
						<ScanSettingsFilters>
							{filterAttributes.map(attr => {
								return <ScanFilterTag key={attr.name} attribute={attr} onClose={removeAttribute} />
							})}
						</ScanSettingsFilters>
					)}
					<AddFilter>
						<Button
							type="primary"
							onClick={() => {
								if(isTrialAccount){
									setPaymentModalOpen(true, "filters")
								}
								else {
									setCurrentFilterForm(NEW_FILTER)
									setFilterModalVisible(true)
								}
							}}
						><Icon type="plus" /> ADD FILTER</Button>
					</AddFilter>
					<ScanSettingsFilterActions>
						<FilterAction
							icon="download"
							onClick={() => {
								if(isTrialAccount){
									setPaymentModalOpen(true, "filters")
								}
								else {
									setCurrentFilterForm(SAVE_FILTER)
								setFilterModalVisible(true)
								}
							}}
						>
							SAVE FILTER
						</FilterAction>
						<StyledDivider />
						<FilterAction
							icon="upload"
							onClick={() => {
								if(isTrialAccount){
									setPaymentModalOpen(true, "filters")
								}
								else {
									setCurrentFilterForm(LOAD_FILTER)
									setFilterModalVisible(true)
								}
							}}
						>
							LOAD FILTER
						</FilterAction>
					</ScanSettingsFilterActions>
				</ScanSettingsFilterBar>
			</ContentContainer>
			<Modal
				centered
				width={600}
				title={formTitles[currentFilterForm]}
				visible={filterModalVisible}
				onCancel={() => setFilterModalVisible(false)}
				footer={
					<FormFooter
						onOK={formCallbacks[currentFilterForm]}
						onClose={() => setFilterModalVisible(false)}
						okText={okTexts[currentFilterForm]}
					/>
				}
			>
				<Form layout="vertical">
					{currentFilterForm === NEW_FILTER && <NewFilter form={form} isScanCompare={isScanCompare} isDetailsView={isDetailsView} />}
					{currentFilterForm === SAVE_FILTER && <SaveFilter form={form} fieldKey={SAVE_FILTER} />}
					{currentFilterForm === LOAD_FILTER && <LoadFilter form={form} fieldKey={LOAD_FILTER} filters={filters} />}
				</Form>
			</Modal>
		</Fragment>
	)
}

export default Form.create({})(ScanFilterBar)
